// Shared singleton ticket store for /tickets/* pages.
//
// Solves the duplicate-fetch problem: every bucket, rail, and consumer
// that previously called fetchGeneralTriage() independently now subscribes
// to a single in-memory store. The store:
//
//   1. Fetches triage data once (paginated initial load, limit=100 first page)
//   2. Deduplicates concurrent callers — if two pages call getStore() at the
//      same time, only one fetch fires; both get the same Promise.
//   3. Reference-counts subscribers. When the last subscriber leaves,
//      the realtime channel is torn down and the store is cleared after
//      a 5s grace period (covers SPA page transitions).
//   4. Exposes a reactive update mechanism: subscribers register callbacks
//      that receive the new triage object whenever state changes.
//   5. Buffers realtime events (200ms debounce) so rapid INSERT/UPDATE
//      storms from TEMO don't trigger N refetches.
//
// Usage from a page:
//
//   import { getTicketStore } from '../functions/tickets/ticketStore.js'
//
//   // In onCreate:
//   const store = getTicketStore()
//   el._storeUnsub = store.subscribe((triage) => {
//     s.root.update({ triage })
//   })
//   // If data already loaded, push it immediately:
//   if (store.current()) s.root.update({ triage: store.current() })
//
//   // In onRemove:
//   if (el._storeUnsub) { el._storeUnsub(); el._storeUnsub = null }

import { fetchGeneralTriage, fetchTicketPage, computeTriage } from './fetchGeneralTriage.js'
import { subscribeTicketsRealtime } from './subscribeTicketsRealtime.js'

// Grace period before clearing store after last subscriber leaves.
const GRACE_MS = 5_000
// Max age before we consider cached data stale and refetch.
const STALE_MS = 60_000

let _triage = null          // current triage object
let _fetchedAt = 0          // epoch ms of last successful fetch
let _fetchPromise = null    // in-flight fetch promise (dedup)
let _subscribers = new Set()
let _rtUnsub = null
let _graceClearTimer = null
let _errorCount = 0

const _notify = (triage) => {
  for (const fn of _subscribers) {
    try { fn(triage) } catch (_) {}
  }
}

const _teardownRealtime = () => {
  if (_rtUnsub) { _rtUnsub(); _rtUnsub = null }
}

const _scheduleGraceClear = () => {
  if (_graceClearTimer) clearTimeout(_graceClearTimer)
  _graceClearTimer = setTimeout(() => {
    // Only clear if still no subscribers (could re-subscribe during grace).
    if (_subscribers.size === 0) {
      _triage = null
      _fetchedAt = 0
      _fetchPromise = null
      _errorCount = 0
      _teardownRealtime()
    }
    _graceClearTimer = null
  }, GRACE_MS)
}

const _setupRealtime = () => {
  if (_rtUnsub) return  // already subscribed
  _rtUnsub = subscribeTicketsRealtime({
    pageName: 'store',
    tables: ['tickets', 'agent_presence', 'agent_activity_log'],
    onRefresh: async () => {
      if (_subscribers.size === 0) return
      try {
        const next = await fetchGeneralTriage()
        _triage = next
        _fetchedAt = Date.now()
        _errorCount = 0
        _notify(next)
      } catch (err) {
        console.error('[ticketStore] realtime refresh error', err)
        _errorCount++
      }
    }
  })
}

// Load triage if needed (not loaded, stale, or in error state).
const _maybeLoad = async () => {
  const now = Date.now()
  const isStale = _fetchedAt > 0 && (now - _fetchedAt) > STALE_MS
  if (_triage && !isStale) return _triage
  if (_fetchPromise) return _fetchPromise

  // Fast path: initial limited fetch for first paint.
  // After the fast path resolves, schedule a background full refresh if
  // the server has more tickets than our initial page.
  _fetchPromise = fetchGeneralTriage({ full: false })
    .then(async (triage) => {
      _triage = triage
      _fetchedAt = Date.now()
      _fetchPromise = null
      _errorCount = 0
      _notify(triage)

      // Background full refresh if initial page was capped.
      if (triage._totalAvailable > triage._initialLimit && _subscribers.size > 0) {
        setTimeout(async () => {
          if (_subscribers.size === 0) return
          try {
            const full = await fetchGeneralTriage({ full: true })
            if (_subscribers.size === 0) return
            _triage = full
            _fetchedAt = Date.now()
            _notify(full)
          } catch (err) {
            console.warn('[ticketStore] background full-refresh error', err)
          }
        }, 200)  // slight delay so initial paint settles
      }

      return triage
    })
    .catch(err => {
      console.error('[ticketStore] fetch error', err)
      _fetchPromise = null
      _errorCount++
      return _triage  // return stale if available
    })

  return _fetchPromise
}

// Public API — singleton store.
export const getTicketStore = () => {
  return {
    // Subscribe to triage updates. Returns an unsubscribe function.
    subscribe (fn) {
      _subscribers.add(fn)
      // Cancel any pending grace-clear (a subscriber just came back).
      if (_graceClearTimer) { clearTimeout(_graceClearTimer); _graceClearTimer = null }
      // Start realtime if first subscriber.
      if (_subscribers.size === 1) _setupRealtime()
      // Kick off load (or use cached).
      _maybeLoad()
      // Return unsubscribe.
      return () => {
        _subscribers.delete(fn)
        if (_subscribers.size === 0) _scheduleGraceClear()
      }
    },

    // Synchronously return current triage (may be null before first load).
    current () { return _triage },

    // Force a full refetch (e.g., after the user approves a ticket).
    async refresh () {
      _fetchedAt = 0  // mark stale to force re-fetch
      return _maybeLoad()
    },

    // Apply an optimistic patch directly (for immediate UI feedback).
    patch (triage) {
      _triage = triage
      _notify(triage)
    },

    // Expose error count so UI can show retry CTA.
    errorCount () { return _errorCount }
  }
}

// Singleton instance exported directly for convenience.
export const ticketStore = getTicketStore()
